import type { CurrentFiltersType } from "@/types/common/filters";
import axios from "@/utils/axios";
import { SOURCE_ORDER } from "@/workflow/components/configurations/data-center/context/sources-helper";
import type { DataCenterSource } from "@/workflow/components/configurations/data-center/types";
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
  QueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";

export interface TableColumn {
  id: string;
  name: string;
  type: string;
}
export interface Table {
  id: string;
  name: string;
  description: string;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
  source?: DataCenterSource;
  columns?: Array<TableColumn>;
  table_display_name?: string;
}

export interface PaginationLink {
  url: string | null;
  label: string;
  active: boolean;
}

export interface Meta {
  current_page: number;
  from: number;
  last_page: number;
  links: PaginationLink[];
  path: string;
  per_page: number;
  to: number;
  total: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  links: {
    first: string;
    last: string;
    prev: string | null;
    next: string | null;
  };
  meta: Meta;
}

export interface GetTablesParams {
  page?: number;
  per_page?: number;
  filters?: CurrentFiltersType[];
  order_by?: string;
  orderDirection: string;
  search?: string;
  created_at?: string;
  updated_at?: string;
  role_id?: string;
  columns?: 0 | 1;
}

export interface ColumnsEditData {
  id: string;
  name: string;
  type: "STRING" | "INTEGER" | "BOOLEAN" | "FLOAT";
  display_name: string;
}

export type NewColumnData = Pick<
  ColumnsEditData,
  "name" | "type" | "display_name"
>;

const getSourceEndpoint = (source: DataCenterSource) => {
  switch (source) {
    case "Orquestador":
      return "/admin/v1/data-center/sources/ORQUESTADOR/tables";
    case "Operador":
      return "/admin/v1/data-center/sources/OPERADOR/tables";
    case "sdt":
    default:
      return "/admin/v1/data-center/tables";
  }
};

const appendSourceToTables = (
  data: PaginatedResponse<Table>,
  source: DataCenterSource,
): PaginatedResponse<Table> => {
  return {
    ...data,
    data: data.data.map((table) => ({
      ...table,
      id: source === "sdt" ? table.id : table.name,
      source,
    })),
  };
};

export const useGetTablesWithSource = (
  source: DataCenterSource,
  params?: GetTablesParams,
) => {
  return useQuery({
    queryKey: ["data-center", "tables", params, source],
    placeholderData: keepPreviousData,
    retry: 0,
    enabled: params?.page !== 0,
    queryFn: async (): Promise<PaginatedResponse<Table>> => {
      const {
        page,
        per_page,
        filters,
        order_by,
        search,
        role_id,
        orderDirection,
        columns,
      } = params || {};

      const status = filters?.find((obj) => obj.status);
      const updated_at = filters?.find(
        (obj) => obj.updated_at !== undefined,
      )?.updated_at;
      const created_at = filters?.find(
        (obj) => obj.created_at !== undefined,
      )?.created_at;
      const statusValue = status?.status;

      let trashedFilter: string | undefined;

      if (statusValue === "ACTIVE") {
        trashedFilter = "";
      } else if (statusValue === "ACTIVE,INACTIVE") {
        trashedFilter = "with";
      } else if (statusValue === "INACTIVE") {
        trashedFilter = "only";
      }

      const queryParams = {
        ...(page && { page }),
        ...(per_page && { per_page }),
        ...(trashedFilter && { "filter[trashed]": trashedFilter }),
        ...(order_by && {
          order_by: orderDirection === "desc" ? `!${order_by}` : order_by,
        }),
        ...(updated_at && { ["filter[updated_at]"]: updated_at }),
        ...(created_at && { ["filter[created_at]"]: created_at }),
        ...(role_id && { role: role_id }),
        ...(columns !== undefined && { "filter[columns]": columns }),
        filter: { ...(search && { search }) },
      };
      if (search && search.trim() !== "") {
        // Chain API calls for all sources
        const allResults = await Promise.all(
          SOURCE_ORDER.map(async (sourceType) => {
            try {
              const response = await axios.get(getSourceEndpoint(sourceType), {
                params: queryParams,
              });
              return appendSourceToTables(response.data, sourceType);
            } catch {
              // Return empty result for failed sources
              return {
                data: [],
                links: { first: "", last: "", prev: null, next: null },
                meta: {
                  current_page: 1,
                  from: 0,
                  last_page: 1,
                  links: [],
                  path: "",
                  per_page: per_page || 20,
                  to: 0,
                  total: 0,
                },
              };
            }
          }),
        );

        // Combine all results
        const combinedData = allResults.flatMap((result) => result.data);
        const totalResults = combinedData.length;
        return {
          data: combinedData,
          links: {
            first: allResults[0]?.links.first || "",
            last: allResults[0]?.links.last || "",
            prev: null,
            next: null,
          },
          meta: {
            current_page: 1,
            from: totalResults > 0 ? 1 : 0,
            last_page: 1,
            links: [],
            path: "",
            per_page: totalResults,
            to: totalResults,
            total: totalResults,
          },
        };
      }

      // Default case: Single source query
      const response = await axios.get(getSourceEndpoint(source), {
        params: queryParams,
      });
      return appendSourceToTables(response.data, source);
    },
  });
};

export const useGetTables = (params?: GetTablesParams) => {
  return useQuery({
    queryKey: ["data-center", "tables", params],
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<PaginatedResponse<Table>> => {
      const {
        page,
        per_page,
        filters,
        order_by,
        search,
        role_id,
        orderDirection,
        columns,
      } = params || {};

      const status = filters?.find((obj) => obj.status);
      const updated_at = filters?.find(
        (obj) => obj.updated_at !== undefined,
      )?.updated_at;
      const created_at = filters?.find(
        (obj) => obj.created_at !== undefined,
      )?.created_at;
      const statusValue = status?.status;

      let trashedFilter: string | undefined;

      if (statusValue === "ACTIVE") {
        trashedFilter = "";
      } else if (statusValue === "ACTIVE,INACTIVE") {
        trashedFilter = "with";
      } else if (statusValue === "INACTIVE") {
        trashedFilter = "only";
      }

      const queryParams = {
        ...(page && { page }),
        ...(per_page && { per_page }),
        ...(trashedFilter && { "filter[trashed]": trashedFilter }),
        ...(order_by && {
          order_by: orderDirection === "desc" ? `!${order_by}` : order_by,
        }),
        ...(updated_at && { ["filter[updated_at]"]: updated_at }),
        ...(created_at && { ["filter[created_at]"]: created_at }),
        ...(role_id && { role: role_id }),
        ...(columns !== undefined && { "filter[columns]": columns }),
        filter: { ...(search && { search }) },
      };

      const response = await axios.get("/admin/v1/data-center/tables", {
        params: queryParams,
      });
      return response.data;
    },
  });
};

type addDataToTable = Record<string, string | boolean | number>;

// Crear una tabla
export const useCreateTable = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      name: string;
      table_display_name?: string;
      description?: string;
    }) => {
      const response = await axios.post("/admin/v1/data-center/tables", data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["data-center", "tables"] });
    },
  });
};

// Editar una tabla (data)
export const useEditTableData = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      table_name,
      data,
    }: {
      id: string;
      table_name: string;
      data: addDataToTable;
    }) => {
      const response = await axios.patch(
        `/admin/v1/data-center/tables/data/${id}`,
        { table_name, data },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["data-center", "tables"] });
    },
  });
};

export const useAddDataToTable = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      table_name,
      data,
    }: {
      table_name: string;
      data: addDataToTable;
    }) => {
      const response = await axios.post(
        `/admin/v1/data-center/tables/add-data`,
        { table_name, data },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["data-center", "tables"] });
    },
  });
};

export const useUpdateRowData = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      table_name,
      data,
    }: {
      id: string;
      table_name: string;
      data: addDataToTable;
    }) => {
      const response = await axios.patch(
        `/admin/v1/data-center/tables/data/${id}`,
        { table_name, data },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["data-center", "tables"] });
    },
  });
};

export const useDeleteRowData = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      table_name,
    }: {
      id: string;
      table_name: string;
    }) => {
      const response = await axios.delete(
        `/admin/v1/data-center/tables/data/${id}`,
        { data: { table_name } },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["data-center", "tables"] });
    },
  });
};

export const useDeleteMultipleRowsData = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      ids,
      table_name,
    }: {
      ids: string[];
      table_name: string;
    }) => {
      const response = await axios.patch(
        `/admin/v1/data-center/tables/data/massive-delete`,
        { records_ids: ids, table_name },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["data-center", "tables"] });
    },
  });
};

// Eliminar una tabla (data)
export const useDeleteTableData = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      table_name,
    }: {
      id: string;
      table_name: string;
    }) => {
      const response = await axios.delete(
        `/admin/v1/data-center/tables/data/${id}`,
        { data: { table_name } },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["data-center", "tables"] });
    },
  });
};

// Obtener información de una tabla (por nombre)
export const useGetTableData = (
  name: string,
  source: string = "SDT",
  paginate: number = 0,
  filters:
    | Array<{
        name: string;
        operator?: string;
        type: string;
        value: string | number | boolean | null;
      }>
    | undefined
    | null = [],
) => {
  const normalizedFilters = filters || [];

  return useQuery({
    queryKey: [
      "data-center",
      "table-data",
      name,
      source,
      paginate,
      normalizedFilters,
    ],
    queryFn: async (): Promise<{
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data: any;
    }> => {
      const response = await axios.get("/admin/v1/data-center/tables/data", {
        params: {
          table_name: name,
          source,
          filters: normalizedFilters,
          paginate,
        },
      });
      return response.data;
    },
    enabled: !!name,
  });
};

export const useGetTableDataInfinite = ({
  name,
  source = "SDT",
  search = "",
  order_by,
  per_page = 60,
}: {
  name: string;
  source?: string;
  search?: string;
  order_by?: string;
  per_page?: number;
}) => {
  return useInfiniteQuery({
    queryKey: [
      "data-center",
      "table-data-infinite",
      name,
      source,
      search,
      order_by,
      per_page,
    ],
    queryFn: async ({
      pageParam = 1,
    }): Promise<{
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data: any;
      meta: Meta;
    }> => {
      const response = await axios.get("/admin/v1/data-center/tables/data", {
        params: {
          table_name: name,
          source,
          paginate: 1,
          page: pageParam,
          per_page,
          ...(order_by && { order_by }),
          filter: {
            ...(search && { search }),
          },
        },
      });
      return response.data;
    },
    enabled: !!name,
    initialPageParam: 1,
    getNextPageParam: ({ data }) => {
      const { current_page, last_page } = data as Meta;
      if (current_page < last_page) {
        return current_page + 1;
      }
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });
};

export const useGetTableColumnTypes = () => {
  return useQuery({
    queryKey: ["data-center", "table-column-types"],
    queryFn: async () => {
      const response = await axios.get(
        "/admin/v1/data-center/tables/columns-types",
      );
      return response.data;
    },
  });
};

// Actualizar una tabla (por id)
export const useUpdateTable = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      name,
      table_display_name,
      description,
      columns,
    }: {
      id: string;
      name: string;
      table_display_name?: string;
      description?: string;
      columns?: ColumnsEditData[];
    }) => {
      const response = await axios.patch(`/admin/v1/data-center/tables/${id}`, {
        name,
        table_display_name,
        ...(description && { description }),
        columns,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["data-center", "tables"] });
    },
  });
};

//Agregar nueva columna
export const useAddNewColumnInTable = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      name,
      description,
      columns,
    }: {
      id: string;
      name: string;
      description?: string;
      columns: NewColumnData[];
    }) => {
      const response = await axios.patch(`/admin/v1/data-center/tables/${id}`, {
        name,
        ...(description && { description }),
        columns,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["data-center", "tables"] });
    },
  });
};

// Remover una tabla por id específico
export const useRemoveTableById = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const response = await axios.delete(
        `/admin/v1/data-center/tables/${id}`,
        { data: { reason } },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["data-center", "tables"] });
    },
  });
};

export const useReactivateTableById = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.patch(
        `/admin/v1/data-center/tables/${id}/active-table`,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["data-center", "tables"] });
    },
  });
};

// Obtener los tipos de columnas aceptados
export const useGetColumnTypes = () => {
  return useQuery({
    queryKey: ["data-center", "columns-types"],
    queryFn: async () => {
      const response = await axios.get(
        "/admin/v1/data-center/tables/columns-types",
      );
      return response.data;
    },
  });
};

export const getTableDetailsByTableName = async (
  name: string,
  source?: string,
) => {
  if (source && source !== "SDT") {
    const response = await axios.get<{
      data: {
        data: Array<ColumnsEditData>;
      };
    }>(`/admin/v1/data-center/functional-core/columns`, {
      params: {
        table_name: name,
      },
    });
    return response.data;
  } else {
    const response = await axios.get<{
      data: {
        columns: Array<ColumnsEditData>;
      };
    }>(`/admin/v1/data-center/tables/byName/${name}`);
    return response.data;
  }
};

export const useGetTableDetailsByTableName = (
  name: string,
  source?: string,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: ["data-center", "table-details", name, source],
    queryFn: () => getTableDetailsByTableName(name, source),
    enabled: !!name && enabled,
  });
};

export const useGetTableDetail = (table: Table | undefined) => {
  const source = table?.source || "sdt";
  return useQuery({
    queryKey: [
      "data-center",
      "table",
      source === "sdt" ? table?.id : table?.name,
    ],
    queryFn: async () => {
      if (source === "sdt") {
        const response = await axios.get(
          `/admin/v1/data-center/tables/${table?.id}`,
        );
        return response.data;
      } else {
        const response = await axios.get<{
          data: Array<{ name: string; type: string }>;
        }>(
          `admin/v1/data-center/sources/${source.toUpperCase()}/tables/columns`,
          {
            params: {
              table_name: table?.name,
            },
          },
        );
        return {
          data: {
            columns: response.data.data.map((item) => ({
              ...item,
              id: item.name,
            })),
            description: "",
            name: table?.name,
          },
        };
      }
    },
    enabled: source === "sdt" ? !!table?.id : !!table?.name,
  });
};

// Mostrar la info de una tabla por id
export const useGetTableById = (id: string) => {
  return useQuery({
    queryKey: ["data-center", "table", id],
    queryFn: async () => {
      const response = await axios.get(`/admin/v1/data-center/tables/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
};

export const createTableDetailsBreadcrumb = (
  queryClient: QueryClient,
  id: string,
) => {
  return async () => {
    const tableId = id;
    try {
      const table = await queryClient.ensureQueryData({
        queryKey: ["data-center", "table", tableId],
        queryFn: async () => {
          const response = await axios.get(
            `/admin/v1/data-center/tables/${tableId}`,
          );
          return response.data;
        },
        staleTime: 5 * 60 * 1000, // 5 minutos
      });
      return { data: table.data as Table };
    } catch (error) {
      console.log("Error fetching table for breadcrumb:", error);
      return {
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  };
};

export const useGetDataCenterTablesInfinite = (searchString?: string) => {
  return useInfiniteQuery({
    queryKey: ["data-center-tables-infinite", searchString],
    initialPageParam: 1,
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryFn: async ({ pageParam }): Promise<PaginatedResponse<Table>> => {
      const response = await axios.get<PaginatedResponse<Table>>(
        "/admin/v1/data-center/tables",
        {
          params: {
            page: pageParam,
            per_page: 15,
            order_by: "name",
            "filter[columns]": 1,
            ...(searchString && { "filter[search]": searchString }),
          },
        },
      );
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage.meta.current_page;
      const lastPageNum = lastPage.meta.last_page;
      return currentPage < lastPageNum ? currentPage + 1 : undefined;
    },
  });
};

export const getTableData = async (
  table_name: string,
  column_name: string,
  page: number = 1,
) => {
  try {
    if (!table_name || !column_name) return;
    const response = await axios.get("/admin/v1/data-center/tables/data", {
      params: {
        table_name,
        paginate: 1,
        per_page: 15,
        page: page,
      },
    });
    return response?.data?.data;
  } catch (error) {
    console.error(`Error fetching data from table:`, error);
    return [];
  }
};

export const getDataCenterDataById = async (table_name: string, id: string) => {
  const response = await axios.get(`/admin/v1/data-center/tables/data`, {
    params: {
      table_name,
      "filters[0][name]": "id",
      "filters[0][operator]": "EQUALS",
      "filters[0][value]": id,
      "filters[0][type]": "STRING",
    },
  });
  return response?.data;
};

export const useGetDataCenterDataById = (
  tableName: string,
  id: string,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: ["data-center-data-by-id", tableName, id],
    queryFn: async () => {
      return getDataCenterDataById(tableName, id);
    },
    enabled: enabled && !!tableName && !!id,
  });
};
